import { useContext } from "react";

import { Scenario, ScenarioStatus } from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";

export type ScenarioAction =
  | "create"
  | "update"
  | "bulk update"
  | "view"
  | "check"
  | "delete"
  | "request qc"
  | "qc accept/reject"
  | "view qc status"
  | "archive"
  | "unarchive"
  | "manage users";

export function useActionAllowed(scenario: Scenario | null): (action: ScenarioAction) => boolean {
  // Returns a function "actionAllowed" that can be used to determine
  // whether current user is allowed to do a given ScenarioAction. The
  // backend checks that too obviously, but this is used for visual effects like
  // disabling buttons.
  const { hasScope } = useContext(LdoAuthContext);

  const status: ScenarioStatus | null = scenario ? scenario.status : null;

  const isViewer = hasScope("viewer");
  const isAdmin = hasScope("admin");
  const isReviewer = hasScope("reviewer");
  const isManager = hasScope("manager");

  // Split off for testing
  return createActionAllowed(status, isViewer, isAdmin, isReviewer, isManager);
}

export function createActionAllowed(
  status: ScenarioStatus | null,
  isViewer: boolean,
  isAdmin: boolean,
  isReviewer: boolean,
  isManager: boolean
) {
  const isQcRequested =
    status === "quality_control_requested" ||
    status === "quality_rejected" ||
    status === "quality_checked";

  return function actionAllowed(action: ScenarioAction) {
    switch (action) {
      case "create":
        return status === null && isAdmin;
      case "check":
        return isAdmin && status === "complete";
      case "update":
        return (
          isAdmin &&
          (status === "complete" || status === "import_checked" || status === "quality_rejected")
        );
      case "bulk update":
        // With bulk update, we can't check the statuses of the scenarios in the frontend
        return isAdmin;
      case "view":
        return (
          isAdmin ||
          status === "quality_checked" ||
          (isViewer && (status === "import_checked" || isQcRequested)) ||
          (isReviewer && isQcRequested)
        );
      case "view qc status":
        return isQcRequested && (isAdmin || isReviewer);
      case "delete":
        return status === "complete" && isAdmin;
      case "request qc":
        return (status === "import_checked" || status === "quality_rejected") && isAdmin;
      case "qc accept/reject":
        return status === "quality_control_requested" && isReviewer;
      case "archive":
        return (
          (isAdmin &&
            (status === "import_checked" ||
              status === "quality_control_requested" ||
              status === "quality_rejected")) ||
          (isReviewer && status === "quality_checked")
        );
      case "unarchive":
        return isAdmin && status === "archived";
      case "manage users":
        return isManager;
      default:
        return false;
    }
  };
}
